import { Url } from '../types';
import { supabase, getSupabaseClient } from './supabase';

export const urlService = {
  async getUrlsByWebsite(websiteId: string): Promise<{ urls: Url[]; error: string | null }> {
    try {
      console.log('Fetching URLs for website_id:', websiteId);
      
      // Use admin client to bypass RLS
      const { data, error } = await getSupabaseClient(true)
        .from('urls')
        .select('*')
        .eq('website_id', websiteId)
        .order('url_id');

      if (error) {
        console.error('Error fetching URLs:', error);
        return { urls: [], error: error.message };
      }

      console.log('Fetched URLs:', data?.length || 0);
      const urls: Url[] = data || [];

      return { urls, error: null };
    } catch (error: any) {
      console.error('Exception fetching URLs:', error);
      return { urls: [], error: error.message };
    }
  },

  async getUrl(urlId: string): Promise<{ url: Url | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('urls')
        .select('*')
        .eq('url_id', urlId)
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async createUrl(
    urlData: { url_id: string; full_url: string; website_id: string; title?: string; path?: string }
  ): Promise<{ url: Url | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('urls')
        .insert([
          {
            url_id: urlData.url_id,
            full_url: urlData.full_url,
            website_id: urlData.website_id,
            title: urlData.title,
            path: urlData.path,
          },
        ])
        .select()
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async updateUrl(
    urlId: string,
    urlData: { full_url?: string; title?: string; path?: string; meta_description?: string }
  ): Promise<{ url: Url | null; error: string | null }> {
    try {
      const updateData: any = {};
      if (urlData.full_url !== undefined) updateData.full_url = urlData.full_url;
      if (urlData.title !== undefined) updateData.title = urlData.title;
      if (urlData.path !== undefined) updateData.path = urlData.path;
      if (urlData.meta_description !== undefined) updateData.meta_description = urlData.meta_description;
      
      const { data, error } = await supabase
        .from('urls')
        .update(updateData)
        .eq('url_id', urlId)
        .select()
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async deleteUrl(urlId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.from('urls').delete().eq('url_id', urlId);
      if (error) return { error: error.message };
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async bulkCreateUrls(
    urls: Array<{ url_id: string; full_url: string; website_id: string; title?: string; path?: string; meta_description?: string }>
  ): Promise<{ count: number; error: string | null }> {
    try {
      const insertData = urls.map((u) => ({
        url_id: u.url_id,
        full_url: u.full_url,
        website_id: u.website_id,
        title: u.title || null,
        path: u.path || null,
        meta_description: u.meta_description || null,
      }));

      const { data, error } = await supabase.from('urls').insert(insertData as any).select();

      if (error) {
        return { count: 0, error: error.message };
      }

      return { count: data.length, error: null };
    } catch (error: any) {
      return { count: 0, error: error.message };
    }
  },
};